import { useMemo } from 'react';

// Where Google sends the provider after they approve Verca's calendar access.
// googleCalendarCallback redirects here:
//   success          -> /google-calendar
//   anything else    -> /google-calendar?google=cancelled|expired|missing_permission|error

const STATES = {
  connected: {
    icon: 'fa-calendar-check',
    tone: 'success',
    title: 'Google Calendar Successfully Connected',
    body: 'Your bookings on Verca will now sync with your Google Calendar.',
  },
  cancelled: {
    icon: 'fa-calendar-xmark',
    tone: 'neutral',
    title: 'Connection Cancelled',
    body: 'Google Calendar wasn’t connected. You can try again anytime from the Schedule screen in Verca.',
  },
  missing_permission: {
    icon: 'fa-calendar-xmark',
    tone: 'neutral',
    title: 'Calendar Permission Needed',
    body: 'Verca needs permission to manage its calendar. Please connect again and allow calendar access.',
  },
  expired: {
    icon: 'fa-clock-rotate-left',
    tone: 'neutral',
    title: 'Link Expired',
    body: 'The connection took too long to finish. Please return to Verca and connect again.',
  },
  error: {
    icon: 'fa-triangle-exclamation',
    tone: 'neutral',
    title: 'Something Went Wrong',
    body: 'We couldn’t connect your Google Calendar. Please return to Verca and try again.',
  },
};

const GoogleCalendar = () => {
  const state = useMemo(() => {
    const status = new URLSearchParams(window.location.search).get('google');
    return STATES[status] || STATES.connected;
  }, []);

  const isSuccess = state.tone === 'success';

  return (
    <div className="gc-root">
      <nav className="gc-nav">
        <a href="/" className="gc-logo">Verca</a>
      </nav>

      <main className="gc-main">
        <div className="gc-card">
          <div className={`gc-icon ${isSuccess ? 'gc-icon-success' : 'gc-icon-neutral'}`}>
            <i className={`fas ${state.icon}`} aria-hidden="true" />
            {isSuccess && (
              <span className="gc-badge" aria-hidden="true">
                <i className="fas fa-check" />
              </span>
            )}
          </div>

          <h1 className="gc-title">{state.title}</h1>
          <p className="gc-body">{state.body}</p>

          <p className="gc-hint">
            You can close this page and return to Verca.
          </p>
        </div>
      </main>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }

        :root {
          --ink: #0F0E17;
          --text-tertiary: #767676;
          --hairline: #E6E6E6;
          --hairline-soft: #EDEDED;
          --accent: #6BE891;
          --accent-soft: #E6FBEE;
          --accent-deep: #0B7A4B;
        }

        body {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          background: #FFFFFF;
          color: #000000;
          -webkit-font-smoothing: antialiased;
        }

        .gc-root {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: #FFFFFF;
        }

        .gc-nav {
          padding: 14px 24px;
          border-bottom: 1px solid var(--hairline-soft);
        }

        .gc-logo {
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.9px;
          color: #000000;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }

        .gc-logo:hover {
          opacity: 0.65;
        }

        .gc-main {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 16px 64px;
        }

        .gc-card {
          width: 100%;
          max-width: 440px;
          text-align: center;
          animation: gc-rise 0.5s cubic-bezier(0.32, 0, 0.24, 1) both;
        }

        /* Calendar tile with a check badge, same shape as the homepage's
           feature icons. */
        .gc-icon {
          position: relative;
          width: 88px;
          height: 88px;
          margin: 0 auto 28px;
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .gc-icon i {
          font-size: 38px;
        }

        .gc-icon-success {
          background: var(--accent);
        }

        .gc-icon-success > i {
          color: #0A0A0F;
        }

        .gc-icon-neutral {
          background: #F6F6F6;
          border: 1px solid var(--hairline);
        }

        .gc-icon-neutral > i {
          color: var(--text-tertiary);
        }

        .gc-badge {
          position: absolute;
          right: -8px;
          bottom: -8px;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--ink);
          border: 3px solid #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: gc-pop 0.45s cubic-bezier(0.34, 1.45, 0.64, 1) 0.25s both;
        }

        .gc-badge i {
          font-size: 13px;
          color: var(--accent);
        }

        .gc-title {
          font-size: clamp(26px, 6vw, 34px);
          font-weight: 800;
          letter-spacing: -1px;
          line-height: 1.15;
          color: #000000;
          margin-bottom: 14px;
        }

        .gc-body {
          font-size: 17px;
          line-height: 1.6;
          color: var(--text-tertiary);
          margin-bottom: 24px;
        }


        .gc-hint {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-tertiary);
        }

        @keyframes gc-rise {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes gc-pop {
          from { opacity: 0; transform: scale(0.4); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gc-card,
          .gc-badge {
            animation: none;
          }
        }
      `}</style>

      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap" rel="stylesheet" />
    </div>
  );
};

export default GoogleCalendar;